import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable, ViewContainerRef } from '@angular/core';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { AlarmsPatient } from 'src/app/utils/interfaces/new/alarms-patient.interface';
import { AldPatient } from 'src/app/utils/interfaces/new/ald-patient.interface';
import { AllergiesPatient } from 'src/app/utils/interfaces/new/allergies-patient.interface';
import { AntecedentFamilial } from 'src/app/utils/interfaces/new/antecedent-familial.interface';
import { AntecedentPerso } from 'src/app/utils/interfaces/new/antecedent-perso.interface';
import { AntecedentsCount } from 'src/app/utils/interfaces/new/antecedents-count-patient.interface';
import { ContactsPatient } from 'src/app/utils/interfaces/new/contacts-patient';
import { CouverturePatient } from 'src/app/utils/interfaces/new/couverture-patient.interface';
import { CreationPatient } from 'src/app/utils/interfaces/new/creation-patient.interface';
import { DesensibilisationsPatient } from 'src/app/utils/interfaces/new/desensibilsations-patient.interface';
import { DonsPatient } from 'src/app/utils/interfaces/new/dons-patient';
import { FamillePatient } from 'src/app/utils/interfaces/new/famille-patient.interface';
import { AutorisationAlimentationDmp, FichePatient } from 'src/app/utils/interfaces/new/fiche-patient.interface';
import { FiltrePatient } from 'src/app/utils/interfaces/new/filtre-patient.interface';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { HistoMedicoSocialPatient } from 'src/app/utils/interfaces/new/histo-medico-social-patient.interface';
import { IdentityToCheck } from 'src/app/utils/interfaces/new/identity-to-check';
import { InfosPatient } from 'src/app/utils/interfaces/new/infos-patient.interface';
import { NoteAntecedents } from 'src/app/utils/interfaces/new/note-antecedents.interface';
import { NotePatient } from 'src/app/utils/interfaces/new/note-patient.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { PathologiesPatient } from 'src/app/utils/interfaces/new/pathologies-patient.interface';
import { Patient } from 'src/app/utils/interfaces/new/patient.interface';
import { PatientAccessProfil } from 'src/app/utils/interfaces/new/PatientAccessProfil';
import { ResultatPatient } from 'src/app/utils/interfaces/new/resultat-patient.interface';
import { SuiviPatient } from 'src/app/utils/interfaces/new/suivi-patient';
import { VaccinsPatient } from 'src/app/utils/interfaces/new/vaccins-patient.interface';
import { environment } from 'src/environments/environment';
import { FiltresService } from '../filtres/filtres.service';
import { SnackbarService } from '../snackbar/snackbar.service';
import { ModalService } from '../modal/modal.service';
import { ConditionsUtilsService } from '../conditions-utils/conditions-utils.service';
import { LogsDebugService } from '../logs-debug/logs-debug.service';
import { Adresse } from 'src/app/utils/interfaces/new/adresse.interface';
import { PatientVitale } from 'src/app/utils/interfaces/new/patientVitale';

@Injectable({
  providedIn: 'root'
})
export class PatientService {

  public static readonly REXEG_NIR_13 = /^[1-478][0-9]{2}(0[1-9]|1[0-2]|62|63)(2[ABab]|[0-9]{2})(00[1-9]|0[1-9][0-9]|[1-8][0-9]{2}|9[0-8][0-9]|990)(00[1-9]|0[1-9][0-9]|[1-9][0-9]{2})$/;
  public updatePatientInfosEmitter: EventEmitter<any> = new EventEmitter<any>();

  private _wait = (ms) => new Promise(res => setTimeout(res, ms));

  private isMock = environment.mock;

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders(),
    params: undefined
  };

  private apiUri = environment.basePath + '/patient/';

  constructor(
    private _http: HttpClient,
    private _filtresService: FiltresService,
    private _snack: SnackbarService,
    private _modal: ModalService,
    private _logsDebugSrv: LogsDebugService
  ) { }

  async findOnePatientInfos(patientId: string): Promise<InfosPatient> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/infos/' + patientId, this.httpOptions).toPromise();
      const infos = res.body as InfosPatient;
      return infos;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findPrintPatientInfos(patientId: string): Promise<string> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/print/infos/' + patientId, this.httpOptions).toPromise();
      const infos = res.body as string;
      return infos;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientInfos(patientId: string, updateInfos: InfosPatient) {
    try {

      if (patientId !== updateInfos.id) {
        this._snack.openSnackBarError(`Un problème avec l'identification de ce patient est survenue.`);
        await this._logsDebugSrv.addLog({
          content: `Changement d'identité stoppé pour le patient ${patientId} // ${updateInfos.id}`,
          meta: new Error().stack,
        });
        return;
      }

      updateInfos.idJuxta = updateInfos.idJuxta ? updateInfos.idJuxta : undefined;
      updateInfos.idTitre = updateInfos.idTitre ? updateInfos.idTitre : undefined;
      updateInfos.sexe = updateInfos.sexe ? updateInfos.sexe : undefined;
      updateInfos.nomNaissance = updateInfos.nomNaissance ? updateInfos.nomNaissance : undefined;
      updateInfos.idResponsable = updateInfos.idResponsable ? updateInfos.idResponsable : undefined;
      updateInfos.isLocked = updateInfos.isLocked ? updateInfos.isLocked : false;
      updateInfos.aRetenir = updateInfos.aRetenir ? updateInfos.aRetenir : '';
      updateInfos.poids = ConditionsUtilsService.isZeroOrHigher(updateInfos.poids) ? updateInfos.poids : undefined;
      updateInfos.perimetreCranien = ConditionsUtilsService.isZeroOrHigher(updateInfos.perimetreCranien) ? updateInfos.perimetreCranien : undefined;
      updateInfos.taille = ConditionsUtilsService.isZeroOrHigher(updateInfos.taille) ? updateInfos.taille : undefined;
      updateInfos.tauxCreatine = ConditionsUtilsService.isZeroOrHigher(updateInfos.tauxCreatine) ? updateInfos.tauxCreatine : undefined;
      updateInfos.dfg = ConditionsUtilsService.isZeroOrHigher(updateInfos.dfg) ? updateInfos.dfg : undefined;
      updateInfos.consentement = updateInfos.consentement ? updateInfos.consentement : false;
      updateInfos.dmpConsentementConsultation = updateInfos.dmpConsentementConsultation !== undefined ? updateInfos.dmpConsentementConsultation : null;
      updateInfos.dmpPatientInforme = updateInfos.dmpPatientInforme ? updateInfos.dmpPatientInforme : false;
      updateInfos.dmpOppositionAlimentation = updateInfos.dmpOppositionAlimentation ? updateInfos.dmpOppositionAlimentation : false;
      updateInfos.iceBreak = updateInfos.iceBreak ? updateInfos.iceBreak : false;
      updateInfos.idInsuffisanceHepatique = updateInfos.idInsuffisanceHepatique ? updateInfos.idInsuffisanceHepatique : undefined;
      updateInfos.idInsuffisanceCardiaque = updateInfos.idInsuffisanceCardiaque ? updateInfos.idInsuffisanceCardiaque : undefined;
      updateInfos.dateAmenorrhee = updateInfos.dateAmenorrhee ? updateInfos.dateAmenorrhee : undefined;
      updateInfos.grossesse = updateInfos.grossesse ? updateInfos.grossesse : false;
      updateInfos.idAllaitement = updateInfos.idAllaitement ? updateInfos.idAllaitement : undefined;
      updateInfos.numeroDossier = updateInfos.numeroDossier ? updateInfos.numeroDossier : undefined;
      updateInfos.idTag = updateInfos.idTag ? updateInfos.idTag : undefined;
      updateInfos.isMt = updateInfos.isMt ? updateInfos.isMt : false;
      updateInfos.isAld = updateInfos.isAld ? updateInfos.isAld : false;
      updateInfos.isCmu = updateInfos.isCmu ? updateInfos.isCmu : false;
      updateInfos.isOption = updateInfos.isOption ? updateInfos.isOption : false;
      updateInfos.isMutuelle = updateInfos.isMutuelle ? updateInfos.isMutuelle : false;
      updateInfos.isRosp = updateInfos.isRosp ? updateInfos.isRosp : false;
      updateInfos.isInterdit = updateInfos.isInterdit ? updateInfos.isInterdit : false;
      updateInfos.journal = updateInfos.journal ? updateInfos.journal : undefined;
      updateInfos.noAllergies = updateInfos.noAllergies ? updateInfos.noAllergies : false;

      const res = await this._http.put(this.apiUri + 'patients/infos/' + patientId, updateInfos, this.httpOptions).toPromise();
      this.updatePatientInfosEmitter.emit();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findOnePatientFamille(patientId: string): Promise<FamillePatient[]> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/famille/' + patientId, this.httpOptions).toPromise();
      const famille = res.body as FamillePatient[];
      return famille;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientFamille(patientId: string, updateInfos: FamillePatient[]) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/famille/' + patientId, updateInfos, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findOnePatientNote(patientId: string): Promise<NotePatient> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/note/' + patientId, this.httpOptions).toPromise();
      const note = res.body as NotePatient;
      return note;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientNote(patientId: string, updateInfos: NotePatient) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/note/' + patientId, updateInfos, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findOnePatientNoteAntecedents(patientId: string): Promise<NoteAntecedents> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/note-antecedents/' + patientId, this.httpOptions).toPromise();
      const note = res.body as NoteAntecedents;
      return note;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientNoteAntecedents(patientId: string, updateInfos: NoteAntecedents) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/note-antecedents/' + patientId, updateInfos, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findOnePatientFiche(patientId: string): Promise<FichePatient> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/fiche/' + patientId, this.httpOptions).toPromise();

      const fiche = res.body as FichePatient;
      return fiche;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findOnePatientFicheForLogs(patientId: string): Promise<FichePatient | undefined> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/fiche/' + patientId, this.httpOptions).toPromise();
      const fiche = res.body as FichePatient;
      return fiche;
    } catch (error) {
      return undefined;
    }
  }

  async updatePatientFiche(patientId: string, updateFiche: FichePatient, resetIns?: boolean) {
    try {
      const options = { ...this.httpOptions };
      if (resetIns && options['params']) {
        options['params'].resetIns = true;
      } else if (resetIns && !options['params']) {
        options['params'] = { resetIns: true };
      }
      updateFiche.cpNaissance = updateFiche.cpNaissance || undefined;
      updateFiche.ins = updateFiche.ins || undefined;
      updateFiche.insKey = updateFiche.insKey || undefined;
      updateFiche.oid = updateFiche.oid || undefined;
      updateFiche.documentDeValidation = updateFiche.documentDeValidation || undefined;

      const res = await this._http.put(this.apiUri + 'patients/fiche/' + patientId, updateFiche, options).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async getIdentitiesForMassCheck(): Promise<Array<IdentityToCheck>> {
    try {
      const options = { ...this.httpOptions };

      const res = await this._http.get<Array<IdentityToCheck>>(this.apiUri + 'ins/verification-en-masse', options).toPromise();
      return res.body;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findOnePatientCouverture(patientId: string): Promise<CouverturePatient> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/couverture/' + patientId, this.httpOptions).toPromise();
      const couv = res.body as CouverturePatient;
      return couv;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientCouverture(patientId: string, updateCouverture: CouverturePatient) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/couverture/' + patientId, updateCouverture, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findOnePatientDons(patientId: string): Promise<DonsPatient> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/dons/' + patientId, this.httpOptions).toPromise();
      const dons = res.body as DonsPatient;
      return dons;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientDons(patientId: string, updateDons: DonsPatient) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/dons/' + patientId, updateDons, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findOnePatientSuivi(patientId: string): Promise<SuiviPatient> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/suivi/' + patientId, this.httpOptions).toPromise();
      const suivi = res.body as SuiviPatient;
      return suivi;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientSuivi(patientId: string, updateSuivi: SuiviPatient) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/suivi/' + patientId, updateSuivi, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findOnePatientRepertoire(patientId: string): Promise<ContactsPatient> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/repertoire/' + patientId, this.httpOptions).toPromise();
      const contacts = res.body as ContactsPatient;
      return contacts;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientRepertoire(patientId: string, updateRepertoire: ContactsPatient) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/repertoire/' + patientId, updateRepertoire, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findCountAntecedents(patientId: string): Promise<AntecedentsCount[]> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/count-antecedents/' + patientId, this.httpOptions).toPromise();
      const antecedents = res.body as AntecedentsCount[];
      return antecedents;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findPrintPatientAntecedents(patientId: string): Promise<string> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/print/antecedents/' + patientId, this.httpOptions).toPromise();
      const antecedents = res.body as string;
      return antecedents;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOnePatientAntecedentsPerso(patientId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'dateAntecedent'
  }, type?: string): Promise<Paginated<AntecedentPerso>> {
    try {
      const options = { ...this.httpOptions };
      options.headers = this._filtresService.setHeaders(filtre);
      if (type) {
        let params = new HttpParams();
        params = params.append('type', type)
        options.params = params;
      }
      const res = await this._http.get(`${this.apiUri}patients/antecedents/perso/${patientId}`, options).toPromise();
      const antecedents = res.body as AntecedentPerso[];
      const paginated: Paginated<AntecedentPerso> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: antecedents
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientAntecedentsPerso(patientId: string, antecedentsPerso: AntecedentPerso[]) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/antecedents/perso/' + patientId, antecedentsPerso, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOnePatientAntecedentsFamilial(patientId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'creationDate'
  }): Promise<Paginated<AntecedentFamilial>> {
    try {
      const options = { ...this.httpOptions };
      options.headers = this._filtresService.setHeaders(filtre);
      const res = await this._http.get(`${this.apiUri}patients/antecedents/familial/${patientId}`, options).toPromise();
      const antecedents = res.body as AntecedentFamilial[];
      const paginated: Paginated<AntecedentFamilial> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: antecedents
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientAntecedentsFamilial(patientId: string, antecedentsFamilial: AntecedentFamilial[]) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/antecedents/familial/' + patientId, antecedentsFamilial, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOnePatientAllergies(patientId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'dateAllergie'
  }): Promise<Paginated<AllergiesPatient>> {
    try {
      const options = { ...this.httpOptions };
      options.headers = this._filtresService.setHeaders(filtre);
      const res = await this._http.get(`${this.apiUri}patients/allergies/${patientId}`, options).toPromise();
      const allergies = res.body as AllergiesPatient[];
      const paginated: Paginated<AllergiesPatient> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: allergies
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientAllergies(patientId: string, allergiesPatient: AllergiesPatient[]) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/allergies/' + patientId, allergiesPatient, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOnePatientPathologies(patientId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'datePathologie'
  }): Promise<Paginated<PathologiesPatient>> {
    try {
      const options = { ...this.httpOptions };
      options.headers = this._filtresService.setHeaders(filtre);
      const res = await this._http.get(`${this.apiUri}patients/pathologies/${patientId}`, options).toPromise();
      const pathologies = res.body as PathologiesPatient[];
      const paginated: Paginated<PathologiesPatient> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: pathologies
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }


  async updatePatientPathologies(patientId: string, pathologiesPatient: PathologiesPatient[]) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/pathologies/' + patientId, pathologiesPatient, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOnePatientHistoMedicoSocial(patientId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'dateHistorique'
  }): Promise<Paginated<HistoMedicoSocialPatient>> {
    try {
      const options = { ...this.httpOptions };
      options.headers = this._filtresService.setHeaders(filtre);
      const res = await this._http.get(`${this.apiUri}patients/histo-medico-social/${patientId}`, options).toPromise();
      const histo = res.body as HistoMedicoSocialPatient[];
      const paginated: Paginated<HistoMedicoSocialPatient> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: histo
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }


  async updatePatientHistoMedicoSocial(patientId: string, histoPatient: HistoMedicoSocialPatient[]) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/histo-medico-social/' + patientId, histoPatient, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findOnePatientALD(patientId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'dateDemande'
  }): Promise<Paginated<AldPatient>> {
    try {
      const options = { ...this.httpOptions };
      options.headers = this._filtresService.setHeaders(filtre);
      const res = await this._http.get(this.apiUri + 'patients/ald/' + patientId, options).toPromise();
      const ald = res.body as AldPatient[];
      const paginated: Paginated<AldPatient> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: ald
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientALD(patientId: string, aldPatient: AldPatient[]) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/ald/' + patientId, aldPatient, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updateSharingMedicalData(patientId: string, updateSharingData: InfosPatient) {
    try {
      const res = await this._http.put(`${this.apiUri}patients/data/${patientId}`, updateSharingData, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findOnePatientVaccins(patientId: string, filtre?: Filtres): Promise<VaccinsPatient[]> {
    try {
      const options = { ...this.httpOptions };
      if (filtre) {
        options.headers = this._filtresService.setHeaders(filtre);
      }
      const res = await this._http.get(this.apiUri + 'patients/vaccins/' + patientId, options).toPromise();
      const vaccins = res.body as VaccinsPatient[];
      return vaccins;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findPrintPatientVaccins(patientId: string): Promise<string> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/print/vaccins/' + patientId, this.httpOptions).toPromise();
      const infos = res.body as string;
      return infos;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientVaccins(patientId: string, updateVaccins: VaccinsPatient[]): Promise<Array<VaccinsPatient>> {
    try {
      const res = await this._http.put<Array<VaccinsPatient>>(this.apiUri + 'patients/vaccins/' + patientId, updateVaccins, this.httpOptions).toPromise();
      return res.body;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findOnePatientDesensibilisations(patientId: string): Promise<DesensibilisationsPatient[]> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/desensibilisations/' + patientId, this.httpOptions).toPromise();
      const desensibilisations = res.body as DesensibilisationsPatient[];
      return desensibilisations;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findPrintPatientDesensibilisations(patientId: string): Promise<string> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/print/desensibilisations/' + patientId, this.httpOptions).toPromise();
      const infos = res.body as string;
      return infos;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientDesensibilisations(patientId: string, updateDesensibilisations: DesensibilisationsPatient[]) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/desensibilisations/' + patientId, updateDesensibilisations, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findOnePatientAlarmes(patientId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'dateAlarme'
  }): Promise<Paginated<AlarmsPatient>> {
    try {
      const options = { ...this.httpOptions };
      options.headers = this._filtresService.setHeaders(filtre);
      const res = await this._http.get(this.apiUri + 'patients/alarmes/' + patientId, options).toPromise();
      const alarms = res.body as AlarmsPatient[];
      const paginated: Paginated<AlarmsPatient> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: alarms
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async findPrintPatientAlarmes(patientId: string): Promise<string> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/print/alarmes/' + patientId, this.httpOptions).toPromise();
      const alarms = res.body as string;
      return alarms;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updatePatientAlarme(patientId: string, updateAlarme: AlarmsPatient[]) {
    try {
      const res = await this._http.put(this.apiUri + 'patients/alarmes/' + patientId, updateAlarme, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBarError(error.error.message);
      throw error.error;
    }
  }

  async findPatientAlarmes(patientId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortBy: 'dateAlarme'
  }, processed?: boolean, view?: boolean): Promise<Paginated<AlarmsPatient>> {
    try {
      const options = { ...this.httpOptions };
      options.headers = this._filtresService.setHeaders(filtre);
      options.params = {};
      if (processed) {
        options.params.processed = true;
      }
      if (view) {
        options.params.view = true;
      }
      const res = await this._http.get(this.apiUri + 'patients/alarmes/' + patientId, options).toPromise();
      const alarms = res.body as AlarmsPatient[];
      const paginated: Paginated<AlarmsPatient> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: alarms
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async patientsSearchFilters(filtres: FiltrePatient, pageFiltres: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'nom'
  }): Promise<Paginated<ResultatPatient>> {
    try {
      let headers: HttpHeaders = new HttpHeaders();
      headers = this._filtresService.setHeaders(pageFiltres);
      const options = { ...this.httpOptions };
      options.headers = headers;
      const res = await this._http.post(this.apiUri + 'patients/search/', filtres, options).toPromise();
      const patients = res.body as ResultatPatient[];
      const paginated: Paginated<ResultatPatient> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: patients
      }
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async patientsInlineSearch(inlineSearch: string, pageFiltres: Filtres = {
    perpage: 10,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'nom'
  }): Promise<Paginated<ResultatPatient>> {
    try {
      const options = { ...this.httpOptions };
      let headers: HttpHeaders = new HttpHeaders();
      headers = this._filtresService.setHeaders(pageFiltres);
      options.headers = headers;
      let params = new HttpParams()
        .set('inline', inlineSearch);
      options.params = params;

      const res = await this._http.get(this.apiUri + 'patients/inline-search', options).toPromise();
      const patients = res.body && res.body[0] ? res.body[0] as ResultatPatient[] : [];
      const paginated: Paginated<ResultatPatient> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: patients
      }
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async createPatient(patient: CreationPatient, forceCreatePatient = false): Promise<string> {
    const _getIncs = (p: CreationPatient): string | undefined => {
      if (p.insc && p.insc !== '') {
        return p.insc;
      } else if (p.ins && p.ins !== ''
        && p.insKey && p.insKey !== '') {
        return `${p.ins}${p.insKey}${environment.dmpInsSuffix}`;
      } else {
        return undefined;
      }
    };

    patient.insc = _getIncs(patient);

    try {
      const options = { ...this.httpOptions };

      if (forceCreatePatient) {
        options.params = {
          forceCreate: true
        };
      }

      patient.cpNaissance = patient.cpNaissance || undefined;
      patient.ins = patient.ins || undefined;
      patient.insKey = patient.insKey || undefined;
      patient.oid = patient.oid || undefined;
      patient.documentDeValidation = patient.documentDeValidation || undefined;

      const res = await this._http.post(this.apiUri + 'patients/', patient, options).toPromise();
      const idPatient = res.body as string;
      return idPatient;
    } catch (error) {
      this._snack.openSnackBarError(error.error.message);
      throw error.error;
    }
  }

  async findAll(): Promise<Patient[]> {
    try {
      const res = await this._http.get(this.apiUri + 'patients', this.httpOptions).toPromise();
      const patients = res.body as Patient[];
      return patients;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findLastPatient(): Promise<InfosPatient> {
    try {
      const res = await this._http.get(this.apiUri + 'patients/last', this.httpOptions).toPromise();
      const infos = res.body as InfosPatient;
      return infos;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getPatientProfilWithAccesRight(patientId: string): Promise<Array<PatientAccessProfil>> {
    return new Promise<Array<PatientAccessProfil>>(async (resolve, reject) => {
      try {
        const rtd = await this._http.get<Array<PatientAccessProfil>>(`${this.apiUri}profil/${patientId}`, this.httpOptions).toPromise();
        resolve(rtd.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async putAutorisationAlimentationDmp(autorisationParam: AutorisationAlimentationDmp): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        await this._http.put<void>(`${this.apiUri}autorisation-alimentation-dmp/${autorisationParam.patientId}`, {
          rpps: autorisationParam.rpps, autorisation: autorisationParam.autorisation
        }, this.httpOptions).toPromise();
        resolve();
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async findNoCodedAllergiesAndPathologies(patientId: string): Promise<AllergiesPathologiesNoCoded> {
    return new Promise<AllergiesPathologiesNoCoded>(async (resolve, reject) => {
      try {
        const isCoded = await this._http.get<AllergiesPathologiesNoCoded>(`${this.apiUri}patients/allergies-pathologies/non-codees/${patientId}`, this.httpOptions).toPromise();
        resolve(isCoded.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async checkAllergiesPathologiesAreCoded(view: ViewContainerRef, patientId: string): Promise<void> {
    const coded = await this.findNoCodedAllergiesAndPathologies(patientId);
    if (coded && !coded.allIsCoded) {
      let mes = '';
      if (coded.allergiesNoCoded && coded.allergiesNoCoded.length > 0) {
        mes += 'Les allergies suivantes ne sont pas codées : \n';
        for (const allergie of coded.allergiesNoCoded) {
          mes += `- ${allergie}\n`;
        }
      }

      if (coded.pathologiesNoCoded && coded.pathologiesNoCoded.length > 0) {
        mes += '\nLes pathologies suivantes ne sont pas codées : \n';
        for (const patho of coded.pathologiesNoCoded) {
          mes += `- ${patho}\n`;
        }
      }

      mes += '\n\nCelles-ci ne seront pas utilisée pour la vérification de la posologie.';
      const modal = this._modal.openAlert(view, 'Attention', mes);

      return new Promise<void>(resolve => {
        const sub = modal.instance.closeEmitter.subscribe(() => {
          sub.unsubscribe();
          modal.destroy();
          resolve()
        });
      });
    }
  }

  async findOnePatientAdresses(patientId: string): Promise<Adresse[]> {
    try {
      const res = await this._http.get<Adresse[]>(`${this.apiUri}patients/adresses/${patientId}`, this.httpOptions).toPromise();
      return res.body || [];
    } catch (error) {
      throw error;
    }
  }

  async updateOnePatientAdresses(patientId: string, adresses: Adresse[]) {
    try {
      await this._http.put<void>(`${this.apiUri}patients/adresses/${patientId}`, adresses, this.httpOptions).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async getPatientVitale(filtrePatientVitale: FiltrePatientVitale): Promise<PatientVitale | null> {
    try {
      const res = await this._http.post<PatientVitale>(`${this.apiUri}patients/vitale/lookup`, filtrePatientVitale, this.httpOptions).toPromise();
      return res.body || null;
    } catch (error) {
      this._snack.openSnackBar(error.message);
      throw error;
    }
  }

  async getPatientVitaleByIdPatient(patientId: string): Promise<PatientVitale | null> {
    try {
      const res = await this._http.get<PatientVitale>(this.apiUri + 'patients/vitale/lookup/' + patientId, this.httpOptions).toPromise();
      return res.body || null;
    } catch (error) {
      this._snack.openSnackBar(error.message);
      throw error;
    }
  }

  async createPatientVitale(PatientVitale: PatientVitale): Promise<string> {
    try {
      const res = await this._http.post(`${this.apiUri}patients/vitale`, PatientVitale, this.httpOptions).toPromise();
      // aucun retour juste le code http
      return '';
    } catch (error) {
      this._snack.openSnackBar(error.message);
      throw error;
    }
  }



  public async generateCda(idPatient: string, formValue: any, format: string): Promise<{ xmlBase64: string }> {
    const res = await this._http.post<{ xmlBase64: string }>(`${this.apiUri}cda/generate`, {
      patientId: idPatient,
      formValue,
      format
    }, this.httpOptions).toPromise();

    return res.body as { xmlBase64: string };
  }

  public async getVaccinPrefill(idPatient: string): Promise<Record<string, string>> {
    const res = await this._http.get<Record<string, string>>(
      `${this.apiUri}cda/vaccinPrefill`,
      { ...this.httpOptions, params: { patientId: idPatient } }
    ).toPromise();
    return res.body as Record<string, string>;
  }


  async deletePatientVitale(patientId: string): Promise<boolean> {
    try {
      const res = await this._http.delete(`${this.apiUri}patients/vitale/${patientId}`, this.httpOptions).toPromise();
      return res.body as boolean;
    } catch (error) {
      this._snack.openSnackBar(error.message);
      throw error;
    }
  }

  async findPatientIdentityStats(filtre: FiltreStatsIdentites): Promise<PatientIdentityStats> {
    try {
      const options = { ...this.httpOptions };
      const res = await this._http.post<PatientIdentityStats>(`${this.apiUri}patients/identity-stats`, filtre, options).toPromise();
      return res.body;
    } catch (error) {
      this._snack.openSnackBarError(error?.error?.message ?? error?.message);
      throw error;
    }
  }
  async findStructuresExerciceCoordonneStats(filtre: FiltreStatsIdentites): Promise<StructuresExerciceCoordonneStats> {
    try {
      const options = { ...this.httpOptions };
      const res = await this._http.post<StructuresExerciceCoordonneStats>(`${this.apiUri}patients/structures-exercice-coordonne-stats`, filtre, options).toPromise();
      return res.body;
    } catch (error) {
      this._snack.openSnackBarError(error?.error?.message ?? error?.message);
      throw error;
    }
  }

  async exportStructuresExerciceCoordonneStats(filtre: FiltreStatsIdentites): Promise<Blob> {
    try {
      const res = await this._http.post(`${this.apiUri}patients/structures-exercice-coordonne-stats/export`, filtre, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
        withCredentials: true,
        responseType: 'blob',
        observe: 'response'
      }).toPromise();
      return res.body as Blob;
    } catch (error) {
      this._snack.openSnackBarError(error?.error?.message ?? error?.message);
      throw error;
    }
  }
}


export interface AllergiesPathologiesNoCoded {
  allIsCoded: boolean;
  allergiesNoCoded: string[];
  pathologiesNoCoded: string[];
}

export interface FiltrePatientVitale {
  immatriculation: string;
  dateNaissance: string; //format JFSE YYYYMMDD
  rangGemellaire: string;
};

export interface PatientIdentityStats {
  total: number;
  totalConsultations: number;
  provisoire: number;
  recuperees: number;
  validees: number;
  qualifiees: number;
  qualifieesTransitoires: number;
  fictifsOuDouteux: number;
  tauxProvisoire: number;
  tauxRecuperees: number;
  tauxValidees: number;
  tauxQualifiees: number;
  tauxQualifieesTransitoires: number;
}

export interface FiltreStatsIdentites {
  finess?: string;
  rpps?: string;
  dateDebut?: string;
  dateFin?: string;
}

export interface StructuresExerciceCoordonneStats {
  indicateurProtocoles: number;
  indicateurEnfants: number | null;
  indicateurMedecinTraitant: number | null;
  indicateurRcp: number;
  ratioRcp: number;
  isSpecialiste: boolean;
}
